import { TimeRangePickerProps } from '@duxton-web/ui';
import dayjs, { Dayjs } from 'dayjs';

import { Filter, Group } from '@/common/components/FilterDrawer/FilterDrawer';
import { FilterType } from '@/common/const/filterTypes';
import { SelectType } from '@/common/const/selectTypes';

export * from './testing';

const shouldDisableDate = (date: dayjs.Dayjs | undefined) => {
  if (date == undefined) {
    return false;
  }
  const today = dayjs();
  return date.isAfter(today.endOf('day'));
};

const getNormalizedTimeRange = (range: [Dayjs, Dayjs]): [Dayjs, Dayjs] => {
  const start = range[0].startOf('day');
  const end = range[1].endOf('day');
  return [start, end];
};

export enum ChangelogType {
  ROLLOUT = 'rollout',
  VARIABLE = 'variable',
}

export enum ChangelogDiffType {
  BEFORE = 'before',
  AFTER = 'after',
}

export const ROLLOUT_DESCRIPTION_MAX_LENGTH = 250;
export const ROLLOUT_REQUEST_LINK_MAX_LENGTH = 100;

export enum ChangelogState {
  NONE = 'none',
  ADDED = 'added',
  MODIFIED = 'modified',
  DELETED = 'deleted',
}

export const globalChangelogRangePresets: TimeRangePickerProps['presets'] = [
  {
    label: 'Last 24 hours',
    value: [dayjs().subtract(1, 'day'), dayjs()],
  },
  {
    label: 'Last 3 days',
    value: getNormalizedTimeRange([
      dayjs().subtract(3, 'day').add(1, 'day'),
      dayjs(),
    ]),
  },
  {
    label: 'Last week',
    value: getNormalizedTimeRange([
      dayjs().subtract(1, 'week').add(1, 'day'),
      dayjs(),
    ]),
  },
];

const localChangelogRangePresets: TimeRangePickerProps['presets'] = [
  ...globalChangelogRangePresets,
  {
    label: 'Last 2 weeks',
    value: getNormalizedTimeRange([
      dayjs().subtract(2, 'week').add(1, 'day'),
      dayjs(),
    ]),
  },
  {
    label: 'Last month',
    value: getNormalizedTimeRange([
      dayjs().subtract(1, 'month').add(1, 'day'),
      dayjs(),
    ]),
  },
  {
    label: 'Last 3 months',
    value: getNormalizedTimeRange([
      dayjs().subtract(3, 'month').add(1, 'day'),
      dayjs(),
    ]),
  },
  {
    label: 'Last year',
    value: getNormalizedTimeRange([
      dayjs().subtract(1, 'year').add(1, 'day'),
      dayjs(),
    ]),
  },
];

export const CommonChangelogFilters: Filter[] = [
  {
    label: 'Change type',
    name: 'changelogType',
    options: [
      {
        label: 'Rollout Changes',
        value: ChangelogType.ROLLOUT,
      },
      {
        label: 'Variable Settings Changes',
        value: ChangelogType.VARIABLE,
      },
    ],
    placeholder: 'Select change type',
  },
  {
    label: 'Changed by',
    name: 'changedBy',
    selectType: SelectType.USER_V2,
    placeholder: 'Select changed by',
  },
  {
    label: 'Approved by',
    name: 'approvedBy',
    selectType: SelectType.USER_V2,
    placeholder: 'Select approved by',
  },
];

export const ConstraintFilterGroup: Group = {
  label: 'Constraints',
  fields: [
    {
      label: 'Constraint name',
      name: 'constraint',
      type: FilterType.CONSTRAINTS,
    },
  ],
};

export const GlobalOnlyChangelogFilters: Filter[] = [
  {
    label: 'Variable Changed',
    name: 'variableName',
    selectType: SelectType.VARIABLE,
    placeholder: 'Select variable changed',
  },
];

export const VariableOnlyChangelogFilters: Filter[] = [
  {
    label: 'Created by',
    name: 'createdBy',
    selectType: SelectType.USER_V2,
    placeholder: 'Select created by',
  },
  {
    label: 'Product owner',
    name: 'productOwner',
    selectType: SelectType.USER_V2,
    placeholder: 'Select product owner',
  },
  {
    label: 'Product name',
    name: 'productEntity',
    selectType: SelectType.PRODUCT_ENTITY,
    placeholder: 'Select product name',
  },
  {
    label: 'Engineering owner',
    name: 'technicalOwner',
    selectType: SelectType.USER_V2,
    placeholder: 'Select engineering owner',
  },
  {
    label: 'Tech family',
    name: 'techFamily',
    selectType: SelectType.TECH_FAMILY,
    placeholder: 'Select tech family',
  },
  {
    label: 'Domain',
    name: 'domain',
    selectType: SelectType.DOMAIN,
    placeholder: 'Select domain',
  },
  {
    label: 'Primary Service',
    name: 'backend',
    selectType: SelectType.BACKEND,
    placeholder: 'Select primary service',
  },
  {
    label: 'Subscribed Services',
    name: 'subscribedServices',
    selectType: SelectType.SUBSCRIBED_SERVICES,
    placeholder: 'Select subscribed services',
  },
];

export const GlobalChangelogFiltersGroup: Group[] = [
  {
    label: 'Change details',
    fields: [
      {
        label: 'Changed At',
        name: 'dates',
        type: FilterType.DATE_TIME_RANGE,
        disabledDate: shouldDisableDate,
        rangePresets: globalChangelogRangePresets,
      },
      ...CommonChangelogFilters,
      ...GlobalOnlyChangelogFilters,
    ],
  },
  ConstraintFilterGroup,
  {
    label: 'Variable details',
    fields: VariableOnlyChangelogFilters,
  },
];

export const VariableChangelogFiltersGroup: Group[] = [
  {
    label: 'Change details',
    fields: [
      {
        label: 'Changed At',
        name: 'dates',
        type: FilterType.DATE_TIME_RANGE,
        disabledDate: shouldDisableDate,
        rangePresets: localChangelogRangePresets,
      },
      ...CommonChangelogFilters,
    ],
  },
  ConstraintFilterGroup,
];
